/**
 * Match bank-statement rows to open invoices (#2897).
 *
 * Priority: exact payment reference, then outstanding amount plus a fuzzy
 * payer-name overlap. Two rows never consume more than one invoice's
 * outstanding balance.
 */

export type MatchConfidence = 'reference' | 'amount_name' | 'none';

export interface StatementMatchRow {
  id: string;
  amountCents: number;
  reference: string | null;
  payerName: string | null;
}

export interface OpenInvoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  totalCents: number;
  paidAmountCents: number;
  paymentReferenceNumber?: string | null;
}

export interface PaymentMatch {
  rowId: string;
  invoiceId: string | null;
  confidence: MatchConfidence;
  isPartial: boolean;
}


const COMPANY_SUFFIXES = new Set([
  'oy',
  'ab',
  'tmi',
  'ky',
  'oyj',
  'ay',
  'kb',
  'hb',
  'gmbh',
  'ltd',
  'inc',
  'llc',
  'osakeyhtio',
  'osakeyhtiö',
  'toiminimi',
  'and',
  'och',
]);

/** Compact a Finnish or RF reference for equality: no spaces, no RF+check, no leading zeros. */
export function normalizeMatchingReference(value: string): string {
  let compact = value.replace(/\s+/g, '').toUpperCase();
  if (/^RF\d{2}/.test(compact)) {
    compact = compact.slice(4);
  }
  compact = compact.replace(/\D/g, '');
  compact = compact.replace(/^0+/, '');
  return compact;
}

/**
 * Reference keys an invoice can be matched by: ONLY its stored payment
 * reference. Deriving one from the invoice number (digits + a computed check
 * digit) was a guess, and a 'reference' match is preselected for recording —
 * a wrong guess would book money on the wrong invoice (#2897 review).
 */
export function invoiceReferenceKeys(invoice: OpenInvoice): Set<string> {
  const keys = new Set<string>();
  if (invoice.paymentReferenceNumber) {
    const normalized = normalizeMatchingReference(invoice.paymentReferenceNumber);
    if (normalized) keys.add(normalized);
  }
  return keys;
}

export function nameTokens(value: string): Set<string> {
  const tokens = value
    .toLowerCase()
    .split(/[^a-z0-9åäöüß]+/i)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2 && !COMPANY_SUFFIXES.has(token));
  return new Set(tokens);
}

export function namesOverlap(payerName: string, recipientName: string): boolean {
  const payer = nameTokens(payerName);
  const recipient = nameTokens(recipientName);
  if (payer.size === 0 || recipient.size === 0) return false;
  for (const token of payer) {
    if (recipient.has(token)) return true;
  }
  return false;
}

function outstandingCents(invoice: OpenInvoice): number {
  return invoice.totalCents - invoice.paidAmountCents;
}

function unmatchedMatch(rowId: string): PaymentMatch {
  return { rowId, invoiceId: null, confidence: 'none', isPartial: false };
}

/**
 * Assign each statement row to at most one open invoice.
 * Reference matches run first; amount+name only considers leftover rows and
 * leftover outstanding cents.
 */
export function matchPaymentsToInvoices(
  rows: StatementMatchRow[],
  openInvoices: OpenInvoice[],
): PaymentMatch[] {
  const remaining = new Map(openInvoices.map((invoice) => [invoice.id, outstandingCents(invoice)]));
  const matches: PaymentMatch[] = rows.map((row) => unmatchedMatch(row.id));

  const claim = (index: number, invoice: OpenInvoice, confidence: MatchConfidence): boolean => {
    const left = remaining.get(invoice.id) ?? 0;
    const row = rows[index];
    if (!row || left <= 0 || row.amountCents > left) return false;
    matches[index] = {
      rowId: row.id,
      invoiceId: invoice.id,
      confidence,
      isPartial: row.amountCents < left,
    };
    remaining.set(invoice.id, left - row.amountCents);
    return true;
  };

  rows.forEach((row, index) => {
    if (!row.reference) return;
    const key = normalizeMatchingReference(row.reference);
    if (!key) return;
    const invoice = openInvoices.find((candidate) => {
      const left = remaining.get(candidate.id) ?? 0;
      return left > 0 && invoiceReferenceKeys(candidate).has(key);
    });
    if (invoice) claim(index, invoice, 'reference');
  });

  rows.forEach((row, index) => {
    if (matches[index]?.confidence !== 'none') return;
    if (!row.payerName) return;
    const invoice = openInvoices.find((candidate) => {
      const left = remaining.get(candidate.id) ?? 0;
      return left === row.amountCents && namesOverlap(row.payerName ?? '', candidate.customerName);
    });
    if (invoice) claim(index, invoice, 'amount_name');
  });

  return matches;
}
